/* =============================================================================
 * M.M.LaFleur — Bundle builder (sections/mm_bundle_builder.liquid).
 *
 * Built Oct 2026 for the Command Center request "Bundle Functionality": make it
 * obvious she can bundle, and sell her a SUIT or a group (tees, 365 Silk
 * Cashmere…). References were Gathre / Kosas (pick pieces into a tray that
 * counts toward a discount) and Merit (a fixed set built piece by piece).
 *
 * HOW PRICING WORKS — NOTHING NEW IN CHECKOUT
 * The store already runs one pair of automatic discounts per bundle group,
 * each scoped to a collection: buy 2 from the collection, 15% off; buy 3+,
 * 20% off. Each tab here is one of those collections, so whatever the shopper
 * builds is priced by the existing discounts in the bag. The numbers in the
 * tray are a preview of that, using the tier percentages set on the block —
 * keep them in step with the discounts in Shopify admin.
 *
 * TWO MODES PER TAB
 *   mix   any pieces from the collection, Gathre-style.
 *   suit  jacket first, then the bottoms cut from the same fabric in the same
 *         colour are brought forward, then anything else as a third piece.
 *         Matching reads the catalogue's own title convention, "Style - Fabric
 *         :: Colour" (e.g. "Moreland The Traveler Blazer - OrigamiTech ::
 *         Black"), so new styles join without setup.
 *
 * DATA comes from /collections/<handle>/products.json — published products
 * only, in the collection's own sort order, with per-variant availability.
 * Colourways are separate products in this catalogue. In suit mode cards group
 * them by "Style - Fabric" with swatches, like the PLP, because fabric decides
 * the match. In mix mode every colourway is its own tile, in collection order,
 * so re-sorting the collection in admin re-sorts the tab.
 *
 * ADDING sends one /cart/add.js call with every piece. mm-cart.js watches
 * that endpoint and opens the bag drawer itself, so there is one cart path.
 * Each line carries a hidden _bundle property naming the group, for
 * reporting; it is underscored, so the shopper never sees it.
 *
 * Barba swaps <main> on soft navigation, so builders are found by attribute
 * and marked when wired (data-mm-bb-ready) rather than bound once on load.
 *
 * NOV 4 2026 (Bundle Page Edits doc). Size dropdowns; the % boxes follow the
 * open tab; later suit steps stay hidden until she has picked a piece; pieces
 * already in the bag from this bundle (their _bundle property, read from
 * /cart.js) count toward the tier, so she can come back and add a third piece;
 * the mobile tray opens from a "Details" button.
 * Pre-change copy: assets/archive--mm-bundle-builder.pre-nov4.js.txt
 *
 * NOV 4 2026 (Christina's notes on the preview). Step 3 also offers the jackets
 * and bottoms she has not chosen, same fabric first, the rest behind a button;
 * the "Complete your suit" badge is gone; a swatch click on a matching piece no
 * longer snaps back to the jacket's colour; a colour missing from the Swatch
 * colours list uses a crop of its own photo instead of grey; mix tabs show every
 * colour as its own tile. Pre-change copy:
 * assets/archive--mm-bundle-builder.pre-christina-nov4.js.txt
 *
 * NOV 4 2026 (step copy). The label, heading and line over each suit step come
 * from the bundle block's settings (cfg.groups[].steps), so they can be edited
 * in the theme editor. Pre-change copy:
 * assets/archive--mm-bundle-builder.pre-steps-nov4.js.txt
 * ========================================================================== */

(function () {
  "use strict";

  if (window.__mmBundleBuilder) { window.__mmBundleBuilder.scan(); return; }

  var money = function (cents) {
    var v = Math.round(cents) / 100;
    var whole = Math.abs(v - Math.round(v)) < 0.005;
    return "$" + v.toLocaleString("en-US", {
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2
    });
  };

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  function sized(src, w) {
    if (!src) return "";
    return src + (src.indexOf("?") === -1 ? "?" : "&") + "width=" + w;
  }

  function store(key, val) {
    try {
      if (val === undefined) return JSON.parse(window.sessionStorage.getItem(key) || "null");
      window.sessionStorage.setItem(key, JSON.stringify(val));
    } catch (e) { return null; }
    return null;
  }

  /* "Moreland The Traveler Blazer - OrigamiTech :: Black"
       -> style "Moreland The Traveler Blazer", fabric "OrigamiTech", colour "Black" */
  function parseTitle(title) {
    var t = String(title || "");
    var colour = "";
    var head = t;
    var cut = t.lastIndexOf(" :: ");
    if (cut !== -1) { head = t.slice(0, cut); colour = t.slice(cut + 4).trim(); }
    var style = head;
    var fabric = "";
    var dash = head.indexOf(" - ");
    if (dash !== -1) { style = head.slice(0, dash).trim(); fabric = head.slice(dash + 3).trim(); }
    return { key: head.trim(), style: style, fabric: fabric, colour: colour };
  }

  function categoryOf(p) {
    var tag = (p.tags || []).filter(function (t) { return /^Category:/i.test(t); })[0];
    var c = tag ? tag.split(":")[1].trim() : (p.product_type || "Other");
    if (/^jacket$/i.test(c)) c = "Jackets";
    return c || "Other";
  }

  function role(cat) {
    if (/jacket|blazer/i.test(cat)) return "jacket";
    if (/pant|trouser|skirt|dress|short|culotte/i.test(cat)) return "bottom";
    return "other";
  }

  function sizeIndex(p) {
    var opts = p.options || [];
    for (var i = 0; i < opts.length; i++) {
      if (/size/i.test(opts[i].name || "")) return i;
    }
    return -1;
  }

  /* Product photos, without swatch files. products.json carries no alt text, so
     the "| Swatch" flag cannot be read here; the filename is what is left. */
  function photos(p) {
    var all = p.images || [];
    var kept = all.filter(function (im) { return !/swatch/i.test(String(im.src || "").split("?")[0]); });
    return kept.length ? kept : all;
  }

  /* Products -> cards, each holding its colourways. eachColour gives every
     product its own card instead of grouping by "Style - Fabric" (see DATA). */
  function normalise(products, eachColour) {
    var groups = [];
    var byKey = {};
    products.forEach(function (p) {
      var avail = (p.variants || []).some(function (v) { return v.available; });
      if (!avail) return;
      var meta = parseTitle(p.title);
      var key = eachColour ? p.handle : meta.key;
      var si = sizeIndex(p);
      var pics = photos(p);
      var cw = {
        id: p.id,
        handle: p.handle,
        style: meta.style,
        fabric: meta.fabric,
        colour: meta.colour || "",
        image: pics[0] ? pics[0].src : "",
        hover: pics[1] ? pics[1].src : "",
        variants: (p.variants || []).map(function (v) {
          var size = si === -1 ? "" : v["option" + (si + 1)];
          if (/^(o\/s|one size|os|default title)$/i.test(size || "")) size = "";
          return {
            id: v.id,
            size: size || "",
            available: !!v.available,
            price: Math.round(parseFloat(v.price) * 100),
            compare: v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : 0
          };
        })
      };
      cw.price = Math.min.apply(null, cw.variants.map(function (v) { return v.price; }));
      var g = byKey[key];
      if (!g) {
        g = byKey[key] = {
          key: key,
          style: meta.style,
          fabric: meta.fabric,
          category: categoryOf(p),
          role: role(categoryOf(p)),
          colourways: []
        };
        groups.push(g);
      }
      g.colourways.push(cw);
    });
    return groups;
  }

  /* --------------------------------------------------------------------- */

  function Builder(root) {
    this.root = root;
    this.cfg = JSON.parse(root.querySelector("[data-mm-bb-config]").textContent);
    this.colourMap = {};
    var self = this;
    (this.cfg.colorMap || "").split("\n").forEach(function (line) {
      var bits = line.split("|");
      if (bits.length > 1 && bits[0].trim()) self.colourMap[bits[0].trim().toLowerCase()] = bits[1].trim();
    });
    this.data = {};          /* group id -> style groups */
    this.loading = {};
    this.trays = {};         /* group id -> [pieces] */
    this.ui = {};            /* group id -> { filter, steer, showAll, showAllThird, picks: {key: {cw, size}} } */
    this.inBag = {};         /* group label -> pieces already in the cart from that bundle */
    this.active = null;
    this.tray = root.querySelector("[data-mm-bb-tray]");
    this.live = root.querySelector("[data-mm-bb-live]");
    this.bind();

    var params = new URLSearchParams(window.location.search);
    var wanted = params.get("bundle");
    var first = this.cfg.groups[0];
    var pick = this.cfg.groups.filter(function (g) { return g.slug === wanted; })[0] || first;
    if (pick) this.select(pick.id, false);
    this.refreshBag();
  }

  Builder.prototype.group = function (id) {
    return this.cfg.groups.filter(function (g) { return g.id === id; })[0];
  };

  Builder.prototype.state = function (id) {
    if (!this.ui[id]) this.ui[id] = { filter: "All", steer: null, showAll: false, showAllThird: false, picks: {} };
    return this.ui[id];
  };

  Builder.prototype.pieces = function (id) {
    if (!this.trays[id]) this.trays[id] = store("mm-bb:" + id) || [];
    return this.trays[id];
  };

  Builder.prototype.save = function (id) { store("mm-bb:" + id, this.trays[id] || []); };

  Builder.prototype.announce = function (msg) {
    if (!this.live) return;
    this.live.textContent = "";
    var live = this.live;
    window.setTimeout(function () { live.textContent = msg; }, 60);
  };

  /* --- what is already in the bag ----------------------------------------- */

  Builder.prototype.bagCount = function (id) {
    var g = this.group(id);
    return g ? (this.inBag[g.label] || 0) : 0;
  };

  /* Pieces added earlier still count toward the discount in the bag, so they
     count here too. Without this the tray emptied on "Add to bag" and a third
     piece asked for "2 more" even though the bag already held two. */
  Builder.prototype.refreshBag = function () {
    var self = this;
    return fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var counts = {};
        (cart.items || []).forEach(function (it) {
          var b = it.properties && it.properties._bundle;
          if (b) counts[b] = (counts[b] || 0) + it.quantity;
        });
        var changed = JSON.stringify(counts) !== JSON.stringify(self.inBag);
        self.inBag = counts;
        if (changed && self.active) {
          self.renderPanel(self.active);
          self.renderTray();
        }
      })
      .catch(function () { /* the tray simply counts its own pieces */ });
  };

  /* --- tabs -------------------------------------------------------------- */

  Builder.prototype.select = function (id, focusTab) {
    this.active = id;
    var g = this.group(id);
    Array.prototype.forEach.call(this.root.querySelectorAll("[data-mm-bb-tab]"), function (t) {
      var on = t.getAttribute("data-mm-bb-tab") === id;
      t.setAttribute("aria-selected", on ? "true" : "false");
      t.tabIndex = on ? 0 : -1;
      if (on && focusTab) t.focus();
    });
    Array.prototype.forEach.call(this.root.querySelectorAll("[data-mm-bb-panel]"), function (p) {
      p.hidden = p.getAttribute("data-mm-bb-panel") !== id;
    });
    /* The % boxes in the page head follow the open bundle. */
    var tiers = this.root.querySelectorAll(".mm-bb-tiers strong");
    if (g && tiers.length > 1) {
      tiers[0].textContent = g.tier2 + "% off";
      tiers[1].textContent = g.tier3 + "% off";
    }
    if (g && window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set("bundle", g.slug);
      window.history.replaceState(window.history.state, "", url.toString());
    }
    this.load(id);
    this.renderTray();
  };

  Builder.prototype.load = function (id) {
    var self = this;
    var g = this.group(id);
    if (!g || this.data[id] || this.loading[id]) { this.renderPanel(id); return; }
    this.loading[id] = true;
    this.renderPanel(id);
    fetch("/collections/" + encodeURIComponent(g.collection) + "/products.json?limit=250", {
      headers: { Accept: "application/json" }
    })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        self.data[id] = normalise(json.products || [], g.mode !== "suit");
        self.loading[id] = false;
        self.renderPanel(id);
      })
      .catch(function () {
        self.loading[id] = false;
        self.data[id] = [];
        self.renderPanel(id, true);
      });
  };

  /* --- product cards ----------------------------------------------------- */

  Builder.prototype.pickFor = function (id, sg) {
    var st = this.state(id);
    if (!st.picks[sg.key]) st.picks[sg.key] = { cw: 0, size: null };
    return st.picks[sg.key];
  };

  /* Listed colours use their hex. Anything else (Fig, Spice, Aubergine...) shows
     a crop of that colourway's own photo, which lands on the garment, so a new
     colour is never a grey dot. */
  Builder.prototype.swatch = function (w) {
    var hex = this.colourMap[(w.colour || "").toLowerCase()];
    if (hex) return "background:" + hex;
    if (w.image) return "background:#d8d2c8 url(" + sized(w.image, 200) + ") 50% 42% / 520% auto no-repeat";
    return "background:#d8d2c8";
  };

  Builder.prototype.card = function (id, sg, opts) {
    opts = opts || {};
    var pick = this.pickFor(id, sg);
    /* Bring a matching piece to the jacket's colour once, not on every render,
       so its swatches still work afterwards. */
    if (opts.colour && pick.steered !== opts.colour) {
      var want = opts.colour.toLowerCase();
      for (var c = 0; c < sg.colourways.length; c++) {
        if (sg.colourways[c].colour.toLowerCase() === want) { pick.cw = c; break; }
      }
      pick.steered = opts.colour;
    }
    var cw = sg.colourways[pick.cw] || sg.colourways[0];
    var sized_ = cw.variants.some(function (v) { return v.size; });
    var chosen = null;
    if (!sized_) chosen = cw.variants.filter(function (v) { return v.available; })[0] || null;
    else if (pick.size) chosen = cw.variants.filter(function (v) { return v.size === pick.size && v.available; })[0] || null;

    var price = chosen ? chosen.price : cw.price;
    var compare = chosen ? chosen.compare : (cw.variants[0] && cw.variants[0].compare) || 0;
    var full = this.pieces(id).length >= this.group(id).max;

    var html = '<article class="mm-bb-card" data-key="' + esc(sg.key) + '" data-slot="' + esc(opts.slot || "") + '">';
    html += '<a class="mm-bb-card-media" href="/products/' + esc(cw.handle) + '" tabindex="-1" aria-hidden="true">';
    html += cw.image ? '<img src="' + esc(sized(cw.image, 600)) + '" alt="" loading="lazy" decoding="async">' : "";
    if (cw.hover) html += '<img class="mm-bb-card-alt" src="' + esc(sized(cw.hover, 600)) + '" alt="" loading="lazy" decoding="async">';
    html += "</a>";
    html += '<div class="mm-bb-card-body">';
    html += '<h3 class="mm-bb-card-title"><a href="/products/' + esc(cw.handle) + '">' + esc(cw.style) + "</a></h3>";
    html += '<p class="mm-bb-card-meta">' + esc([cw.fabric, cw.colour].filter(Boolean).join(" · ")) + "</p>";
    html += '<p class="mm-bb-card-price">' + (compare > price ? "<s>" + money(compare) + "</s> " : "") + money(price) + "</p>";

    if (sg.colourways.length > 1) {
      html += '<div class="mm-bb-swatches" role="group" aria-label="Colour">';
      var self = this;
      sg.colourways.forEach(function (w, i) {
        var name = w.fabric === cw.fabric ? w.colour : w.colour + " · " + w.fabric;
        html += '<button type="button" class="mm-bb-swatch" data-act="colour" data-i="' + i + '" aria-pressed="' + (i === pick.cw ? "true" : "false") +
          '" aria-label="' + esc(name) + '" title="' + esc(name) + '" style="' + esc(self.swatch(w)) + '"></button>';
      });
      html += "</div>";
    }

    if (sized_) {
      html += '<select class="mm-bb-size-select" data-size-select aria-label="Size for ' + esc(cw.style) + '">';
      html += '<option value="">Size</option>';
      cw.variants.forEach(function (v) {
        if (!v.size) return;
        html += '<option value="' + esc(v.size) + '"' + (pick.size === v.size ? " selected" : "") + (v.available ? "" : " disabled") + ">" +
          esc(v.size) + (v.available ? "" : " (sold out)") + "</option>";
      });
      html += "</select>";
    }

    var label = full ? "Bundle is full" : (chosen ? "Add to bundle" : "Select a size");
    html += '<button type="button" class="mm-bb-add" data-act="add"' + (chosen && !full ? "" : " disabled") + ">" + label + "</button>";
    html += "</div></article>";
    return html;
  };

  /* --- panels ------------------------------------------------------------ */

  Builder.prototype.renderPanel = function (id, failed) {
    var panel = this.root.querySelector('[data-mm-bb-panel="' + id + '"] [data-mm-bb-stage]');
    if (!panel) return;
    var g = this.group(id);
    if (failed) { panel.innerHTML = '<p class="mm-bb-empty">These pieces could not be loaded. Please refresh the page.</p>'; return; }
    if (!this.data[id]) {
      var sk = "";
      for (var s = 0; s < 6; s++) sk += '<div class="mm-bb-card is-skeleton"><div class="mm-bb-card-media"></div><div class="mm-bb-card-body"><span></span><span></span></div></div>';
      panel.innerHTML = '<div class="mm-bb-grid" aria-busy="true">' + sk + "</div>";
      return;
    }
    var groups = this.data[id];
    if (!groups.length) { panel.innerHTML = '<p class="mm-bb-empty">Nothing in this bundle is in stock right now.</p>'; return; }
    panel.innerHTML = g.mode === "suit" ? this.suitHtml(id, groups) : this.mixHtml(id, groups);
  };

  Builder.prototype.filterBar = function (id, groups) {
    var st = this.state(id);
    var counts = {};
    groups.forEach(function (sg) { counts[sg.category] = (counts[sg.category] || 0) + 1; });
    var cats = Object.keys(counts);
    if (cats.length < 2) return "";
    var html = '<div class="mm-bb-filters" role="group" aria-label="Filter by category">';
    ["All"].concat(cats).forEach(function (c) {
      html += '<button type="button" class="mm-bb-filter" data-act="filter" data-cat="' + esc(c) + '" aria-pressed="' +
        (st.filter === c ? "true" : "false") + '">' + esc(c) + "</button>";
    });
    return html + "</div>";
  };

  Builder.prototype.mixHtml = function (id, groups) {
    var st = this.state(id);
    var self = this;
    var list = groups.filter(function (sg) { return st.filter === "All" || sg.category === st.filter; });
    var html = this.filterBar(id, groups) + '<div class="mm-bb-grid">';
    list.forEach(function (sg) { html += self.card(id, sg); });
    return html + "</div>";
  };

  /* A step's label, heading and line, from the bundle block's settings. Blank
     ones are left out. attrs goes on the wrapper; note replaces the set line. */
  Builder.prototype.stepHead = function (id, n, attrs, note) {
    var steps = this.group(id).steps || [];
    var s = steps[n - 1] || {};
    var line = note || s.note;
    return '<div class="mm-bb-step"' + (attrs || "") + ">" +
      (s.label ? '<p class="mm-bb-step-no">' + esc(s.label) + "</p>" : "") +
      (s.title ? '<h2 class="mm-bb-step-title">' + esc(s.title) + "</h2>" : "") +
      (line ? '<p class="mm-bb-step-note">' + esc(line) + "</p>" : "") +
      "</div>";
  };

  Builder.prototype.suitHtml = function (id, groups) {
    var st = this.state(id);
    var self = this;
    var jackets = groups.filter(function (sg) { return sg.role === "jacket"; });
    var bottoms = groups.filter(function (sg) { return sg.role === "bottom"; });
    var others = groups.filter(function (sg) { return sg.role === "other"; });
    var base = function (f) { return (f || "").replace(/\s+with\s+.*$/i, "").toLowerCase(); };

    /* The jacket that steers step 2: the one most recently put in the tray from
       step 1, else the last one she added before sending the bundle to her bag.
       An extra jacket added from step 3 does not change step 2 under her. */
    var steer = st.steer;
    var pieces = this.pieces(id);
    for (var i = pieces.length - 1; i >= 0; i--) {
      if (pieces[i].role === "jacket" && !pieces[i].extra) { steer = { fabric: pieces[i].fabric, colour: pieces[i].colour, style: pieces[i].style }; break; }
    }

    var html = this.stepHead(id, 1);
    html += '<div class="mm-bb-grid">';
    jackets.forEach(function (sg) { html += self.card(id, sg); });
    html += "</div>";

    /* Everything after the jacket waits until she has picked a piece. */
    if (!pieces.length && !this.bagCount(id)) return html;

    var matches = [];
    var rest = bottoms;
    if (steer) {
      matches = bottoms.filter(function (sg) {
        return base(sg.fabric) === base(steer.fabric) && sg.colourways.some(function (w) { return w.colour.toLowerCase() === steer.colour.toLowerCase(); });
      });
      rest = bottoms.filter(function (sg) { return matches.indexOf(sg) === -1; });
    }

    /* With nothing to match, say why step 2 is showing everything. This one is
       not copy to edit: it names the jacket she picked. */
    html += this.stepHead(id, 2, " data-mm-bb-step2", steer && !matches.length
      ? "No exact match for your " + steer.style + " in " + steer.colour + ", but every piece below still counts toward the bundle."
      : "");
    html += '<div class="mm-bb-grid">';
    matches.forEach(function (sg) { html += self.card(id, sg, { colour: steer.colour }); });
    if (!steer || st.showAll || !matches.length) {
      rest.forEach(function (sg) { html += self.card(id, sg); });
    }
    html += "</div>";
    if (steer && matches.length && rest.length) {
      html += '<button type="button" class="mm-bb-more" data-act="showall" aria-expanded="' + (st.showAll ? "true" : "false") + '">' +
        (st.showAll ? "Show matching pieces only" : "Show all " + (bottoms.length) + " trousers, skirts & dresses") + "</button>";
    }

    /* Step 3: tops and vests, plus whatever she passed on above. Same fabric as
       her jacket first, everything else behind a button, since the full list is
       most of the collection. */
    var taken = function (sg) {
      return pieces.some(function (p) { return p.style === sg.style && p.fabric === sg.fabric; });
    };
    var free = function (sg) { return !taken(sg); };
    var passed = jackets.concat(bottoms).filter(free);
    var near = steer ? passed.filter(function (sg) { return base(sg.fabric) === base(steer.fabric); }) : passed;
    var far = passed.filter(function (sg) { return near.indexOf(sg) === -1; });
    var third = others.filter(free).concat(near);
    if (!third.length) { third = far; far = []; }
    if (st.showAllThird) third = third.concat(far);

    if (third.length) {
      html += this.stepHead(id, 3);
      html += '<div class="mm-bb-grid">';
      third.forEach(function (sg) { html += self.card(id, sg, { slot: "3" }); });
      html += "</div>";
      if (far.length) {
        html += '<button type="button" class="mm-bb-more" data-act="showallthird" aria-expanded="' + (st.showAllThird ? "true" : "false") + '">' +
          (st.showAllThird ? "Show fewer pieces" : "Show " + far.length + " more pieces") + "</button>";
      }
    }
    return html;
  };

  /* --- tray -------------------------------------------------------------- */

  Builder.prototype.totals = function (id) {
    var g = this.group(id);
    var pieces = this.pieces(id);
    var subtotal = pieces.reduce(function (s, p) { return s + p.price; }, 0);
    var bag = this.bagCount(id);
    var n = pieces.length + bag;
    var pct = n >= 3 ? g.tier3 : (n >= 2 ? g.tier2 : 0);
    var save = Math.round(subtotal * pct / 100);
    return { n: n, inTray: pieces.length, bag: bag, subtotal: subtotal, pct: pct, save: save, total: subtotal - save };
  };

  Builder.prototype.renderTray = function () {
    var id = this.active;
    var g = this.group(id);
    if (!g || !this.tray) return;
    var pieces = this.pieces(id);
    var t = this.totals(id);
    var open = g.max - t.bag;
    var slots = Math.max(Math.max(1, g.slots - t.bag), Math.min(open, pieces.length + (pieces.length < open ? 1 : 0)));

    var status;
    if (t.n === 0) status = "Add 2 pieces to save " + g.tier2 + "%";
    else if (t.n === 1) status = "Add 1 more piece to save " + g.tier2 + "%";
    else if (t.n === 2) status = "Saving " + g.tier2 + "% · add 1 more to save " + g.tier3 + "%";
    else status = "Saving " + g.tier3 + "% — the best bundle price";

    var expanded = this.tray.getAttribute("data-expanded") === "true";
    var html = '<div class="mm-bb-tray-head">';
    html += '<div><p class="mm-eyebrow">Your bundle · ' + esc(g.label) + "</p>";
    html += '<p class="mm-bb-status" data-tier="' + (t.n >= 3 ? 3 : t.n >= 2 ? 2 : 0) + '">' + esc(status) + "</p></div>";
    html += '<button type="button" class="mm-bb-tray-toggle" data-act="tray" aria-expanded="' + (expanded ? "true" : "false") + '">' +
      (expanded ? "Close" : "Details") + "</button>";
    html += "</div>";

    var pctFill = Math.min(100, (Math.min(t.n, 3) / 3) * 100);
    html += '<div class="mm-bb-meter" aria-hidden="true"><span style="width:' + pctFill + '%"></span>' +
      '<i style="left:66.66%"' + (t.n >= 2 ? ' class="is-on"' : "") + '><b>' + g.tier2 + "%</b></i>" +
      '<i style="left:100%"' + (t.n >= 3 ? ' class="is-on"' : "") + '><b>' + g.tier3 + "%</b></i></div>";

    html += '<ol class="mm-bb-slots">';
    if (t.bag) {
      html += '<li class="mm-bb-slot is-bag"><span class="mm-bb-slot-plus" aria-hidden="true">' + t.bag + '</span><p class="mm-bb-slot-empty">' +
        (t.bag === 1 ? "1 piece" : t.bag + " pieces") + " already in your bag</p></li>";
    }
    for (var i = 0; i < slots; i++) {
      var p = pieces[i];
      if (p) {
        html += '<li class="mm-bb-slot is-filled">' +
          (p.image ? '<img src="' + esc(sized(p.image, 160)) + '" alt="">' : "<span></span>") +
          '<div class="mm-bb-slot-info"><p class="mm-bb-slot-name">' + esc(p.style) + "</p>" +
          '<p class="mm-bb-slot-meta">' + esc([p.colour, p.size].filter(Boolean).join(" · ")) + "</p></div>" +
          '<p class="mm-bb-slot-price">' + money(p.price) + "</p>" +
          '<button type="button" class="mm-bb-remove" data-act="remove" data-i="' + i + '" aria-label="Remove ' + esc(p.style) + '">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg></button></li>';
      } else {
        var no = i + t.bag + 1;
        html += '<li class="mm-bb-slot"><span class="mm-bb-slot-plus" aria-hidden="true">+</span><p class="mm-bb-slot-empty">' +
          (no === 3 ? "Piece 3 · " + g.tier3 + "% off" : "Piece " + no) + "</p></li>";
      }
    }
    html += "</ol>";

    html += '<dl class="mm-bb-sum">';
    html += "<div><dt>Subtotal</dt><dd>" + money(t.subtotal) + "</dd></div>";
    html += '<div class="mm-bb-save"><dt>Bundle savings' + (t.pct ? " (" + t.pct + "%)" : "") + "</dt><dd>" + (t.save ? "−" + money(t.save) : "—") + "</dd></div>";
    html += '<div class="mm-bb-total"><dt>Total</dt><dd>' + (t.save ? "<s>" + money(t.subtotal) + "</s> " : "") + money(t.total) + "</dd></div>";
    html += "</dl>";

    var ready = t.inTray >= 1 && t.n >= 2;
    var cta;
    if (ready) cta = (t.bag ? "Add to bag · " : "Add bundle to bag · ") + money(t.total);
    else if (t.n >= 2) cta = "Choose another piece";
    else cta = "Add " + (2 - t.n) + " more to bundle";
    html += '<button type="button" class="mm-bb-cta" data-act="checkout"' + (ready ? "" : " disabled") + ">" + cta + "</button>";
    html += '<p class="mm-bb-fine">' + esc(this.cfg.fine) + "</p>";

    this.tray.innerHTML = html;
    this.tray.setAttribute("data-count", String(t.n));
  };

  /* --- actions ----------------------------------------------------------- */

  Builder.prototype.cardContext = function (el) {
    var card = el.closest(".mm-bb-card");
    var id = this.active;
    if (!card || !this.data[id]) return null;
    var key = card.getAttribute("data-key");
    var sg = this.data[id].filter(function (g) { return g.key === key; })[0];
    return sg ? { id: id, sg: sg, pick: this.pickFor(id, sg), slot: card.getAttribute("data-slot") || "" } : null;
  };

  Builder.prototype.add = function (ctx) {
    var g = this.group(ctx.id);
    var pieces = this.pieces(ctx.id);
    if (pieces.length >= g.max) return;
    var firstPiece = !pieces.length && !this.bagCount(ctx.id);
    var cw = ctx.sg.colourways[ctx.pick.cw] || ctx.sg.colourways[0];
    var v = cw.variants.filter(function (x) {
      return x.available && (!x.size || x.size === ctx.pick.size);
    })[0];
    if (!v) return;
    var extra = ctx.slot === "3";
    pieces.push({
      variant: v.id, price: v.price, size: v.size, colour: cw.colour, image: cw.image,
      style: cw.style, fabric: cw.fabric, role: ctx.sg.role, handle: cw.handle, extra: extra
    });
    if (ctx.sg.role === "jacket" && !extra) {
      this.state(ctx.id).steer = { fabric: cw.fabric, colour: cw.colour, style: cw.style };
    }
    ctx.pick.size = null;
    this.save(ctx.id);
    var t = this.totals(ctx.id);
    this.announce(cw.style + " added. " + t.n + (t.n === 1 ? " piece" : " pieces") + " in your bundle" +
      (t.pct ? ", saving " + t.pct + "%." : "."));
    this.renderPanel(ctx.id);
    this.renderTray();
    this.pulse();
    if (!extra && (firstPiece || ctx.sg.role === "jacket")) this.toStep2();
  };

  /* Step 2 has just appeared or changed below the jackets, so take her there. */
  Builder.prototype.toStep2 = function () {
    var step = this.root.querySelector('[data-mm-bb-panel="' + this.active + '"] [data-mm-bb-step2]');
    if (!step || !step.scrollIntoView) return;
    var calm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    step.scrollIntoView({ behavior: calm ? "auto" : "smooth", block: "start" });
  };

  Builder.prototype.pulse = function () {
    var tray = this.tray;
    tray.removeAttribute("data-pulse");
    void tray.offsetWidth;
    tray.setAttribute("data-pulse", "true");
  };

  Builder.prototype.checkout = function (btn) {
    var id = this.active;
    var g = this.group(id);
    var pieces = this.pieces(id);
    var t = this.totals(id);
    if (t.inTray < 1 || t.n < 2) return;
    var self = this;
    btn.disabled = true;
    btn.textContent = "Adding…";
    var items = pieces.map(function (p) {
      return { id: p.variant, quantity: 1, properties: { _bundle: g.label } };
    });
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || "Could not add to bag"); });
        return r.json();
      })
      .then(function () {
        self.trays[id] = [];
        self.save(id);
        self.inBag[g.label] = t.n;
        self.renderPanel(id);
        self.renderTray();
        self.refreshBag();
        self.announce("Bundle added to your bag.");
      })
      .catch(function (err) {
        btn.disabled = false;
        btn.textContent = err.message || "Something went wrong — try again";
        window.setTimeout(function () { self.renderTray(); }, 2600);
      });
  };

  Builder.prototype.bind = function () {
    var self = this;
    var root = this.root;

    root.addEventListener("click", function (e) {
      var tab = e.target.closest("[data-mm-bb-tab]");
      if (tab) { self.select(tab.getAttribute("data-mm-bb-tab"), false); return; }

      var el = e.target.closest("[data-act]");
      if (!el || el.disabled) return;
      var act = el.getAttribute("data-act");
      var id = self.active;
      var ctx;

      if (act === "colour" && (ctx = self.cardContext(el))) {
        ctx.pick.cw = parseInt(el.getAttribute("data-i"), 10) || 0;
        ctx.pick.size = null;
        self.renderPanel(id);
      } else if (act === "add" && (ctx = self.cardContext(el))) {
        self.add(ctx);
      } else if (act === "filter") {
        self.state(id).filter = el.getAttribute("data-cat");
        self.renderPanel(id);
      } else if (act === "showall") {
        self.state(id).showAll = !self.state(id).showAll;
        self.renderPanel(id);
      } else if (act === "showallthird") {
        self.state(id).showAllThird = !self.state(id).showAllThird;
        self.renderPanel(id);
      } else if (act === "remove") {
        var pieces = self.pieces(id);
        var gone = pieces.splice(parseInt(el.getAttribute("data-i"), 10), 1)[0];
        if (gone && gone.role === "jacket" && !gone.extra && !pieces.some(function (p) { return p.role === "jacket" && !p.extra; })) {
          self.state(id).steer = null;
        }
        self.save(id);
        self.renderPanel(id);
        self.renderTray();
        if (gone) self.announce(gone.style + " removed from your bundle.");
      } else if (act === "tray") {
        var open = self.tray.getAttribute("data-expanded") === "true";
        self.tray.setAttribute("data-expanded", open ? "false" : "true");
        el.setAttribute("aria-expanded", open ? "false" : "true");
        el.textContent = open ? "Details" : "Close";
      } else if (act === "checkout") {
        self.checkout(el);
      }
    });

    /* Size dropdown on a card. The card is rebuilt, so focus moves on to its
       add button, which is the next thing she needs. A piece can be in step 2
       and step 3 at once, so stay on the one she is using. */
    root.addEventListener("change", function (e) {
      var sel = e.target.closest && e.target.closest("[data-size-select]");
      if (!sel) return;
      var ctx = self.cardContext(sel);
      if (!ctx) return;
      ctx.pick.size = sel.value || null;
      var keep = ctx.sg.key;
      self.renderPanel(self.active);
      var again = root.querySelector('.mm-bb-card[data-slot="' + ctx.slot + '"][data-key="' + (window.CSS && CSS.escape ? CSS.escape(keep) : keep) + '"] .mm-bb-add');
      if (again && !again.disabled) again.focus({ preventScroll: true });
    });

    /* Arrow keys between tabs (WAI-ARIA tabs pattern). */
    root.addEventListener("keydown", function (e) {
      var tab = e.target.closest && e.target.closest("[data-mm-bb-tab]");
      if (!tab || (e.key !== "ArrowRight" && e.key !== "ArrowLeft")) return;
      var tabs = Array.prototype.slice.call(root.querySelectorAll("[data-mm-bb-tab]"));
      var i = tabs.indexOf(tab) + (e.key === "ArrowRight" ? 1 : -1);
      var next = tabs[(i + tabs.length) % tabs.length];
      e.preventDefault();
      self.select(next.getAttribute("data-mm-bb-tab"), true);
    });

    /* The bag drawer changed something (a line removed, say): recount. */
    document.addEventListener("mm:cart:rendered", function () {
      if (document.contains(root)) self.refreshBag();
    });
  };

  /* --------------------------------------------------------------------- */

  function scan() {
    Array.prototype.forEach.call(document.querySelectorAll("[data-mm-bb]:not([data-mm-bb-ready])"), function (root) {
      root.setAttribute("data-mm-bb-ready", "");
      try { root.__mmBB = new Builder(root); } catch (err) { root.removeAttribute("data-mm-bb-ready"); }
    });
  }

  window.__mmBundleBuilder = { scan: scan };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", scan);
  else scan();

  document.addEventListener("shopify:section:load", scan);
  new MutationObserver(function () {
    if (document.querySelector("[data-mm-bb]:not([data-mm-bb-ready])")) scan();
  }).observe(document.body, { childList: true, subtree: true });
}());
